<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();

const state = ref('close');
const qrcode = ref(null);
const diagnostics = ref(null);
const isLoadingStatus = ref(false);
const isConnecting = ref(false);
const isRestarting = ref(false);
const isLoggingOut = ref(false);
const isSyncing = ref(false);
const hasDiagnostics = computed(() => Boolean(diagnostics.value));

const stateLabel = computed(() => {
  if (state.value === 'open') {
    return t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.STATE_CONNECTED');
  }

  if (state.value === 'connecting') {
    return t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.STATE_CONNECTING');
  }

  return t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.STATE_DISCONNECTED');
});

const showError = error => {
  useAlert(
    parseAPIErrorResponse(error) ||
      t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.ERROR')
  );
};

const applyDiagnostics = payload => {
  diagnostics.value = payload?.diagnostics || null;
};

const applyPayload = payload => {
  state.value = payload?.state || 'close';
  qrcode.value = payload?.qrcode || null;
  applyDiagnostics(payload);
};

const fetchStatus = async () => {
  isLoadingStatus.value = true;
  try {
    const payload = await store.dispatch('inboxes/getEvolutionStatus', {
      inboxId: props.inbox.id,
    });
    applyPayload(payload);
  } catch (error) {
    showError(error);
  } finally {
    isLoadingStatus.value = false;
  }
};

const generateNewQr = async () => {
  isConnecting.value = true;
  try {
    const payload = await store.dispatch('inboxes/evolutionConnect', {
      inboxId: props.inbox.id,
    });
    applyPayload(payload);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.NEW_QR_SUCCESS'));
  } catch (error) {
    showError(error);
  } finally {
    isConnecting.value = false;
  }
};

const reconnect = async () => {
  isRestarting.value = true;
  try {
    const payload = await store.dispatch('inboxes/evolutionRestart', {
      inboxId: props.inbox.id,
    });
    applyPayload(payload);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.RECONNECT_SUCCESS'));
  } catch (error) {
    showError(error);
  } finally {
    isRestarting.value = false;
  }
};

const logout = async () => {
  isLoggingOut.value = true;
  try {
    const payload = await store.dispatch('inboxes/evolutionLogout', {
      inboxId: props.inbox.id,
    });
    applyPayload(payload);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.LOGOUT_SUCCESS'));
  } catch (error) {
    showError(error);
  } finally {
    isLoggingOut.value = false;
  }
};

const syncNow = async () => {
  isSyncing.value = true;
  try {
    const payload = await store.dispatch('inboxes/evolutionSync', {
      inboxId: props.inbox.id,
    });
    applyDiagnostics(payload);
    const skipped =
      (payload?.skipped_existing || 0) +
      (payload?.skipped_from_me || 0) +
      (payload?.skipped_group || 0) +
      (payload?.skipped_unsupported || 0);

    useAlert(
      t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.SYNC_SUCCESS', {
        imported: payload?.imported || 0,
        skipped,
        failed: payload?.failed || 0,
      })
    );
  } catch (error) {
    showError(error);
  } finally {
    isSyncing.value = false;
  }
};

onMounted(() => {
  fetchStatus();
});
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.TITLE')"
      :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.SUBTITLE')"
    >
      <div class="flex flex-col gap-4">
        <div class="text-sm text-n-slate-11">
          {{
            $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.CONNECTION_STATE', {
              state: stateLabel,
            })
          }}
        </div>

        <div
          v-if="qrcode?.base64"
          class="flex flex-col gap-2 items-center rounded-xl border border-n-weak p-4"
        >
          <img
            :src="qrcode.base64"
            :alt="$t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.QR_ALT')"
            class="size-64 max-w-full object-contain"
          />
          <p class="mb-0 text-xs text-center text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.QR_HELP_TEXT') }}
          </p>
          <p
            v-if="qrcode.pairing_code"
            class="mb-0 text-xs text-center text-n-slate-10"
          >
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.PAIRING_CODE', {
                code: qrcode.pairing_code,
              })
            }}
          </p>
        </div>

        <div v-else class="text-xs text-n-slate-10">
          {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.NO_QR_AVAILABLE') }}
        </div>

        <div
          v-if="hasDiagnostics"
          class="rounded-xl border border-n-weak p-4 text-sm text-n-slate-11"
        >
          <p class="mb-2 font-medium">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.DIAGNOSTICS.TITLE') }}
          </p>
          <p class="mb-1">
            {{
              $t(
                'INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.DIAGNOSTICS.WEBHOOK_URL',
                {
                  value:
                    diagnostics?.webhook_url ||
                    $t(
                      'INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.DIAGNOSTICS.NOT_AVAILABLE'
                    ),
                }
              )
            }}
          </p>
          <p class="mb-1">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.DIAGNOSTICS.LAST_EVENT', {
                event:
                  diagnostics?.last_event ||
                  $t(
                    'INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.DIAGNOSTICS.NOT_AVAILABLE'
                  ),
                at:
                  diagnostics?.last_webhook_at ||
                  $t(
                    'INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.DIAGNOSTICS.NOT_AVAILABLE'
                  ),
              })
            }}
          </p>
          <p class="mb-0">
            {{
              $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.DIAGNOSTICS.LAST_ERROR', {
                error:
                  diagnostics?.last_error ||
                  $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.DIAGNOSTICS.NONE'),
                at:
                  diagnostics?.last_error_at ||
                  $t(
                    'INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.DIAGNOSTICS.NOT_AVAILABLE'
                  ),
              })
            }}
          </p>
        </div>

        <div
          v-if="diagnostics?.dev_warning"
          class="rounded-xl border border-n-amber-6 bg-n-amber-2 p-4 text-sm text-n-amber-11"
        >
          {{ diagnostics.dev_warning }}
        </div>

        <div class="flex flex-wrap gap-3">
          <NextButton :is-loading="isLoadingStatus" @click="fetchStatus">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.REFRESH_STATUS') }}
          </NextButton>
          <NextButton :is-loading="isConnecting" @click="generateNewQr">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.NEW_QR') }}
          </NextButton>
          <NextButton :is-loading="isRestarting" @click="reconnect">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.RECONNECT') }}
          </NextButton>
          <NextButton :is-loading="isLoggingOut" @click="logout">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.LOGOUT') }}
          </NextButton>
          <NextButton :is-loading="isSyncing" @click="syncNow">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.EVOLUTION.SYNC_NOW') }}
          </NextButton>
        </div>
      </div>
    </SettingsSection>
  </div>
</template>
